const IMAGE_MIME_RE = /^image\//i;

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

function buildOptimizedFileName(originalName, mimeType) {
  const name = String(originalName || "image").trim() || "image";
  const base = name.replace(/\.[a-z0-9]+$/i, "");
  const ext = mimeType === "image/webp" ? "webp" : mimeType === "image/png" ? "png" : "jpg";
  return `${base}.${ext}`;
}

function computeTargetSize(width, height, maxWidth, maxHeight) {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return { width: 0, height: 0 };
  }
  const widthRatio = maxWidth > 0 ? maxWidth / width : 1;
  const heightRatio = maxHeight > 0 ? maxHeight / height : 1;
  const ratio = Math.min(1, widthRatio, heightRatio);
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Image decode failed"));
    img.src = url;
  });
}

export async function optimizeImageForUpload(file, options = {}) {
  const {
    maxWidth = 1600,
    maxHeight = 1600,
    quality = 0.82,
    maxOutputBytes = 900 * 1024,
    skipBelowBytes = 200 * 1024,
    outputType = "image/jpeg",
    qualityFloor = 0.45,
  } = options;

  if (!file || !IMAGE_MIME_RE.test(String(file.type || ""))) {
    return file;
  }
  if (typeof window === "undefined" || typeof document === "undefined") {
    return file;
  }

  let objectUrl = "";
  try {
    objectUrl = URL.createObjectURL(file);
    const image = await loadImage(objectUrl);
    const originalWidth = Number(image.naturalWidth) || 0;
    const originalHeight = Number(image.naturalHeight) || 0;
    const target = computeTargetSize(originalWidth, originalHeight, maxWidth, maxHeight);

    const shouldResize = target.width > 0 && target.height > 0 &&
      (target.width !== originalWidth || target.height !== originalHeight);
    if (!shouldResize && Number(file.size) <= skipBelowBytes) {
      return file;
    }

    const canvas = document.createElement("canvas");
    canvas.width = target.width || originalWidth;
    canvas.height = target.height || originalHeight;
    const context = canvas.getContext("2d", { alpha: false });
    if (!context) {
      return file;
    }
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    let workingQuality = quality;
    let blob = await canvasToBlob(canvas, outputType, workingQuality);
    if (!blob) {
      return file;
    }

    while (blob.size > maxOutputBytes && workingQuality > qualityFloor) {
      workingQuality = Math.max(qualityFloor, workingQuality - 0.08);
      const nextBlob = await canvasToBlob(canvas, outputType, workingQuality);
      if (!nextBlob) break;
      blob = nextBlob;
      if (workingQuality <= qualityFloor) break;
    }

    const improvedEnough = blob.size < file.size * 0.98 || shouldResize;
    if (!improvedEnough) {
      return file;
    }

    return new File([blob], buildOptimizedFileName(file.name, blob.type || outputType), {
      type: blob.type || outputType,
      lastModified: Date.now(),
    });
  } catch (_) {
    return file;
  } finally {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
    }
  }
}
